import { SearchEngineInterface } from '@e3labs/isa-data-dictionary';
import api from '../../api';
import { SubsidiaryStockRestrictions } from '~/store/modules/order/order-interface';
class DomainService {
  private fetchDomainList = <T>(
    resource: string,
    { skip, limit, codes }: { skip?: number; limit?: number; codes?: (string | number)[] }
  ) => {
    return api.get<T[]>(resource, {
      params: {
        skip: skip ?? undefined,
        limit: limit ?? undefined,
        externalCodes: codes?.length ? codes.join(',') : undefined
      }
    });
  };
  getActivities() {
    return api.get<any[]>(`/domain/activities`);
  }
  getSubsidiaries({
    limit,
    skip,
    userSubsidiaryFilter
  }: {
    limit?: number;
    skip?: number;
    userSubsidiaryFilter?: string[];
  }) {
    return api.get<any[]>(`/domain/subsidiaries`, {
      params: {
        skip: skip ?? undefined,
        limit: limit ?? undefined,
        userSubsidiaryFilter: userSubsidiaryFilter?.length ? userSubsidiaryFilter?.join(',') : undefined
      }
    });
  }
  getSubsidiaryRestrictions({
    subsidiaryExternalCode,
    subsidiaryStockExternalCode,
    clientState
  }: {
    subsidiaryExternalCode: string;
    subsidiaryStockExternalCode: string;
    clientState: string;
  }) {
    return api.get<SubsidiaryStockRestrictions[]>(`/domain/subsidiary-stock-restrictions`, {
      params: {
        subsidiaryExternalCode,
        subsidiaryStockExternalCode,
        clientState,
        skip: 0,
        limit: 1
      }
    });
  }
  getProviders = (limit?: number, skip?: number, codes?: number[]) => {
    return this.fetchDomainList(`/domain/providers`, { limit, skip, codes });
  };

  postProviders = (limit?: number, skip?: number, externalCodes?: number[]) => {
    return api.post(
      `/domain/providers`,
      {
        externalCodes: externalCodes ?? []
      },
      {
        params: {
          skip: skip ?? undefined,
          limit: limit ?? undefined
        }
      }
    );
  };

  getDepartments = (limit?: number, skip?: number, codes?: number[]) => {
    return this.fetchDomainList(`/domain/departments`, { limit, skip, codes });
  };

  getBrands = (limit?: number, skip?: number, codes?: number[]) => {
    return this.fetchDomainList(`/domain/brands`, { limit, skip, codes });
  };

  getSections = (limit?: number, skip?: number, codes?: number[]) => {
    return this.fetchDomainList(`/domain/sections`, { limit, skip, codes });
  };

  getCategories = (limit?: number, skip?: number, codes?: number[]) => {
    return this.fetchDomainList(`/domain/categories`, { limit, skip, codes });
  };

  getSquares() {
    return api.get<any[]>(`/domain/squares`);
  }
  getPaymentOptions() {
    return api.get<any[]>(`/domain/payment-options`);
  }
  getPaymentPlans(skip?: number, limit?: number) {
    return api.get<any[]>(`/domain/payment-plans`, {
      params: {
        skip: skip ?? undefined,
        limit: limit ?? undefined
      }
    });
  }

  getClientGroups() {
    return api.get<any[]>(`/domain/client-groups`);
  }
  getProductLines = (limit?: number, skip?: number, codes?: number[]) => {
    return this.fetchDomainList(`/domain/product-lines`, { limit, skip, codes });
  };
  getClientPrices({
    skip,
    limit,
    fieldsToShow,
    clientExternalCode,
    subsidiaryExternalCode
  }: {
    skip: number;
    limit: number;
    fieldsToShow?: string;
    clientExternalCode?: string;
    subsidiaryExternalCode?: string;
  }) {
    return api.get<any[]>(`/domain/client-prices`, {
      params: {
        skip,
        limit,
        fieldsToShow,
        clientExternalCode,
        subsidiaryExternalCode
      }
    });
  }
  getOrderTypes() {
    return api.get<any[]>(`/order-type`);
  }
  getSubsidiariesCharge(clientExternalCodes: string) {
    return api.get<any[]>(`/domain/client-payment-option`, {
      params: {
        clientExternalCodes
      }
    });
  }
  getVariables() {
    return api.get<any[]>(`/offline/domain-variable-interest`);
  }
  getPriceTableCodes() {
    return api.get<any[]>(`/product/prices`);
  }

  getProductsMinimized(
    {
      skip,
      limit,
      queryString,
      searchEngine,
      count,
      productExternalCodes
    }: {
      skip: number;
      limit: number;
      queryString?: string;
      searchEngine?: SearchEngineInterface;
      count?: boolean;
      productExternalCodes?: string;
    },
    opts?: any
  ) {
    return api.get<any[]>(`/product/minimized`, {
      params: {
        skip,
        limit,
        queryString,
        searchEngine,
        count,
        productExternalCodes
      },
      ...opts
    });
  }

  getProviderDelivery() {
    return api.get<any[]>(`/domain/providers?type=delivery`);
  }
  getClientAddress({
    addressCode,
    clientExternalCode,
    queryString,
    skip,
    limit,
    count
  }: {
    addressCode?: string;
    clientExternalCode: string;
    queryString?: string;
    skip?: number;
    limit?: number;
    count?: boolean;
  }): any {
    return api.get<any>('/domain/client-address', {
      params: {
        addressCode,
        clientExternalCode,
        queryString,
        skip,
        limit,
        count
      }
    });
  }

  getBonificationTypes(limit?: number, skip?: number): any {
    return api.get<any[]>(`/domain/order-bonification-types`, {
      params: {
        skip: skip ?? undefined,
        limit: limit ?? undefined
      }
    });
  }
}

export default new DomainService();
